// Canonical quantity sets per IFC4 (buildingSMART). The Qto_* names
// are the official property set names that consumers (Solibri, Revit,
// ArchiCAD, BIMcollab, navisworks) look up by hard-coded string. Most
// classes follow the `Qto_<Class>BaseQuantities` convention; a few
// (BuildingElementProxy, CurtainWall) use `Qto_<Class>Quantities`
// without the "Base"; the spec is inconsistent and we mirror it
// faithfully.
//
// IFC2X3 used a less consistent convention (sometimes `Pset_*Quantities`).
// We write IFC4-style names regardless of input schema; modern readers
// accept them, and the alternative would be a per-schema dispatch with
// little benefit since most contemporary IFC files are IFC4 already.

const CLASS_TO_PSET: Record<string, string> = {
  // Building elements
  IfcWall: 'Qto_WallBaseQuantities',
  IfcWallStandardCase: 'Qto_WallBaseQuantities',
  IfcSlab: 'Qto_SlabBaseQuantities',
  IfcSlabStandardCase: 'Qto_SlabBaseQuantities',
  IfcSlabElementedCase: 'Qto_SlabBaseQuantities',
  IfcBeam: 'Qto_BeamBaseQuantities',
  IfcBeamStandardCase: 'Qto_BeamBaseQuantities',
  IfcColumn: 'Qto_ColumnBaseQuantities',
  IfcColumnStandardCase: 'Qto_ColumnBaseQuantities',
  IfcMember: 'Qto_MemberBaseQuantities',
  IfcMemberStandardCase: 'Qto_MemberBaseQuantities',
  IfcPlate: 'Qto_PlateBaseQuantities',
  IfcPlateStandardCase: 'Qto_PlateBaseQuantities',
  IfcPile: 'Qto_PileBaseQuantities',
  IfcFooting: 'Qto_FootingBaseQuantities',
  IfcStair: 'Qto_StairBaseQuantities',
  IfcStairFlight: 'Qto_StairFlightBaseQuantities',
  IfcRamp: 'Qto_RampBaseQuantities',
  IfcRampFlight: 'Qto_RampFlightBaseQuantities',
  IfcRoof: 'Qto_RoofBaseQuantities',
  IfcCovering: 'Qto_CoveringBaseQuantities',
  // Note: spec uses "Quantities" not "BaseQuantities" for these two.
  IfcCurtainWall: 'Qto_CurtainWallQuantities',
  IfcBuildingElementProxy: 'Qto_BuildingElementProxyQuantities',

  // Openings & fixtures
  IfcDoor: 'Qto_DoorBaseQuantities',
  IfcDoorStandardCase: 'Qto_DoorBaseQuantities',
  IfcWindow: 'Qto_WindowBaseQuantities',
  IfcWindowStandardCase: 'Qto_WindowBaseQuantities',
  IfcOpeningElement: 'Qto_OpeningElementBaseQuantities',

  // Spaces
  IfcSpace: 'Qto_SpaceBaseQuantities',
  // IfcZone has no canonical Qto in IFC4 (Pset_ZoneCommon is a
  // property set, not a quantity set). Skip: write only to the
  // VN2R custom set if requested.

  // Reinforcement
  IfcReinforcingBar: 'Qto_ReinforcingBarBaseQuantities',
  IfcReinforcingMesh: 'Qto_ReinforcingMeshBaseQuantities',

  // Distribution / MEP
  IfcPipeSegment: 'Qto_PipeSegmentBaseQuantities',
  IfcPipeFitting: 'Qto_PipeFittingBaseQuantities',
  IfcDuctSegment: 'Qto_DuctSegmentBaseQuantities',
  IfcDuctFitting: 'Qto_DuctFittingBaseQuantities',
  IfcCableSegment: 'Qto_CableSegmentBaseQuantities',
  IfcCableCarrierSegment: 'Qto_CableCarrierSegmentBaseQuantities',

  // Site
  IfcSite: 'Qto_SiteBaseQuantities',

  // Furniture has no canonical Qto in IFC4; leave out so we only
  // write to the VN2R custom set when the user picks "vn2r" or "both".
}

export function psetForClass(ifcClass: string): string | null {
  return CLASS_TO_PSET[ifcClass] ?? null
}

// Custom VN2R quantity set. Prefix `VN2R_` (no `Qto_`) so it's trivial
// to filter in any IFC viewer / property panel: anything starting
// with VN2R was written by this platform.
export function customPsetForClass(ifcClass: string): string {
  const suffix = ifcClass.startsWith('Ifc') ? ifcClass.slice(3) : ifcClass
  return `VN2R_${suffix}BaseQuantities`
}

const QUANTITY_CLASS_BY_NAME: Record<string, string> = {
  Length: 'IfcQuantityLength',
  Width: 'IfcQuantityLength',
  Height: 'IfcQuantityLength',
  Depth: 'IfcQuantityLength',
  Perimeter: 'IfcQuantityLength',
  OverallHeight: 'IfcQuantityLength',
  OverallWidth: 'IfcQuantityLength',
  GrossArea: 'IfcQuantityArea',
  NetArea: 'IfcQuantityArea',
  NetSideArea: 'IfcQuantityArea',
  GrossSideArea: 'IfcQuantityArea',
  NetFloorArea: 'IfcQuantityArea',
  GrossFloorArea: 'IfcQuantityArea',
  NetSurfaceArea: 'IfcQuantityArea',
  OuterSurfaceArea: 'IfcQuantityArea',
  CrossSectionArea: 'IfcQuantityArea',
  ProjectedArea: 'IfcQuantityArea',
  Area: 'IfcQuantityArea',
  NetVolume: 'IfcQuantityVolume',
  GrossVolume: 'IfcQuantityVolume',
  NetWeight: 'IfcQuantityWeight',
  GrossWeight: 'IfcQuantityWeight',
  Count: 'IfcQuantityCount',
}

export function quantityClassFor(name: string): string {
  if (name in QUANTITY_CLASS_BY_NAME) return QUANTITY_CLASS_BY_NAME[name]!
  const lower = name.toLowerCase()
  if (lower.includes('volume')) return 'IfcQuantityVolume'
  if (lower.includes('area')) return 'IfcQuantityArea'
  if (lower.includes('weight') || lower.includes('mass'))
    return 'IfcQuantityWeight'
  if (lower.includes('count')) return 'IfcQuantityCount'
  return 'IfcQuantityLength'
}

const PRIMARY_QUANTITIES_BY_CLASS: Record<string, string[]> = {
  IfcWall: ['Length', 'Width', 'Height', 'NetVolume', 'NetSideArea'],
  IfcWallStandardCase: ['Length', 'Width', 'Height', 'NetVolume', 'NetSideArea'],
  IfcSlab: ['Width', 'Depth', 'NetVolume', 'NetArea', 'GrossArea'],
  IfcBeam: ['Length', 'CrossSectionArea', 'NetVolume', 'OuterSurfaceArea'],
  IfcColumn: ['Length', 'CrossSectionArea', 'NetVolume', 'OuterSurfaceArea'],
  IfcDoor: ['Width', 'Height', 'Area'],
  IfcWindow: ['Width', 'Height', 'Area'],
  IfcSpace: ['Height', 'NetFloorArea', 'GrossFloorArea', 'NetVolume'],
  IfcRoof: ['NetArea', 'GrossArea', 'ProjectedArea'],
}

export function primaryQuantitiesForClass(ifcClass: string): string[] {
  return PRIMARY_QUANTITIES_BY_CLASS[ifcClass] ?? []
}
